import { LoadingSpinner } from '../shared/components';

// ── Team — staff schedules + contact info (UI/UX R16 #7) ────────────────────
// One table everyone can read: who's on which days, and how to reach them.
// Office (admin/director/manager) edit inline; Save writes the whole roster.
// R32: 24-hour coverage strips (Guest Services vs Ops Management) + the ground
// on-call rotation get their own cards above the roster.

const DAYS: Array<[string, string]> = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const esc = (v: any) => String(v == null ? '' : v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

interface Member {
  id: string; name: string; role: string; phone: string; email: string;
  team?: string;   // 'ground' | 'remote' | '' — R30: on-call lives on ground rows
  cov?: string;    // R32: coverage group override — '' (from role) | 'gs' | 'om' | 'both'
  schedule: Record<string, string>; notes: string;
}

// Today's weekday key in business time, for the highlighted column.
const todayKey = (): string => ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).getDay()];

// ── R32 #1: shift parsing for the coverage strips ───────────────────────────
// Cells stay free text; anything that reads like a time range counts toward
// coverage ("8a - 8p", "9-5", "8:30a-5p", "10p-6a", split "8a-12p & 4p-8p").
// Each range is [start, end) in fractional hours — end past 24 = overnight,
// which spills into the NEXT day's strip (the caller handles the spill).
// [] = nothing time-like (off / "on call" / notes / "PTO 8/4-8/9" dates).
const parseShifts = (text: string): Array<[number, number]> => {
  let str = String(text || '');
  if (/\d\s*\/\s*\d/.test(str)) return [];   // "8/4-8/9" is a date, not a shift
  str = str.replace(/noon/gi, '12p').replace(/midnight/gi, '12a');
  const conv = (h: number, min: number, mer?: string): number | null => {
    if (h > 23 || min > 59) return null;
    if (mer) {
      if (h < 1 || h > 12) return null;
      const p = mer[0].toLowerCase() === 'p';
      if (p && h < 12) h += 12;
      if (!p && h === 12) h = 0;
    }
    return h + min / 60;
  };
  const out: Array<[number, number]> = [];
  for (const m of str.matchAll(/(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?\s*(?:-|–|—|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?/gi)) {
    const s = conv(+m[1], +(m[2] || 0), m[3]);
    let e = conv(+m[4], +(m[5] || 0), m[6]);
    if (s == null || e == null) continue;
    // Bare end that lands before the start: prefer the same-day reading when
    // +12 gets past the start ("9-5" → 5p, "5p-9" → 9p), else it's overnight
    // ("10p-6" → 6a next morning). Meridiem'd ends just wrap ("10p - 6a").
    if (!m[6] && e <= s) e += (e + 12 > s) ? 12 : 24;
    else if (e <= s) e += 24;
    out.push([s, e]);
  }
  return out;
};

// Mark [a, b) onto a 24-hour mask (hour h covered when overlapped in [h, h+1)).
const markRange = (acc: boolean[], a: number, b: number): void => {
  for (let h = 0; h < 24; h++) if (a < h + 1 && b > h) acc[h] = true;
};

// R33 #1: uniform time entry. A fractional hour back to "8a" / "8:30p" text,
// and a whole cell back to canonical "8a - 8p" (multi-range joined with " & ").
// Text that doesn't parse ("on call") is left exactly as typed.
const fmtHM = (v: number): string => {
  const t = ((v % 24) + 24) % 24;
  const h = Math.floor(t), min = Math.round((t - h) * 60);
  let hh = h % 12; if (hh === 0) hh = 12;
  return `${hh}${min ? ':' + String(min).padStart(2, '0') : ''}${h < 12 ? 'a' : 'p'}`;
};
const canonicalShift = (text: string): string => {
  const str = String(text || '').trim();
  const ranges = parseShifts(str);
  if (!ranges.length) return str;
  // Only rebuild PURE time cells — "8a-8p (front desk)" keeps its annotation
  // exactly as typed (the coverage parser still counts the range).
  const leftover = str.replace(/noon/gi, '12p').replace(/midnight/gi, '12a')
    .replace(/(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?\s*(?:-|–|—|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?/gi, '')
    .replace(/\band\b|[&,+]/gi, '').trim();
  if (leftover) return str;
  return ranges.map(([s, e]) => `${fmtHM(s)} - ${fmtHM(e)}`).join(' & ');
};
const SHIFT_PRESETS = ['8a - 8p', '9a - 7p', '8a - 6p', '12p - 10p', '8a - 4p', '2p - 10p', '10p - 6a', 'on call'];

const fmtH = (h: number): string => { const x = ((h % 24) + 24) % 24; return x === 0 ? '12a' : x < 12 ? `${x}a` : x === 12 ? '12p' : `${x - 12}p`; };

// Contiguous true-runs of an hour mask as "10p–8a" ranges (midnight wrap merged).
const runsText = (mask: boolean[]): string => {
  if (mask.every(Boolean)) return 'all 24h';
  const runs: Array<[number, number]> = [];
  let start = -1;
  for (let h = 0; h < 24; h++) {
    if (mask[h] && start < 0) start = h;
    if ((!mask[h] || h === 23) && start >= 0) { runs.push([start, mask[h] && h === 23 ? 24 : h]); start = -1; }
  }
  if (runs.length > 1 && runs[0][0] === 0 && runs[runs.length - 1][1] === 24) {
    const last = runs.pop()!;
    runs[0] = [last[0], runs[0][1] + 24];
  }
  return runs.map(([a, b]) => `${fmtH(a)}–${fmtH(b)}`).join(', ');
};

// Which coverage group a person counts toward: explicit override first, else
// derived from the role name (guest/overnight → GS, operations/ops → Ops).
// Ground people never auto-count — the strips answer a REMOTE-desk question
// (a ground ops tech's field hours aren't Ops-Management coverage). An
// explicit override still wins if Jesse wants a ground person counted.
const covGroup = (m: Member): '' | 'gs' | 'om' | 'both' => {
  if (m.cov === 'gs' || m.cov === 'om' || m.cov === 'both') return m.cov;
  if (m.team === 'ground') return '';
  const r = String(m.role || '').toLowerCase();
  const gs = /guest|overnight/.test(r);
  const om = /operation|\bops\b/.test(r);
  return gs && om ? 'both' : gs ? 'gs' : om ? 'om' : '';
};

export class TeamPage {
  private container: HTMLElement;
  private team: Member[] = [];
  private oncall: Record<string, string> = {};   // R32 #2: weekday → ground name
  private oncallLoaded = false;   // only POST oncall when the GET succeeded —
                                  // saving after a failed load must not wipe the stored rotation
  // R114 fix (data-safety #2): the roster saves WHOLE — a failed load shows
  // "couldn't load" with no Edit/Save, and Save carries the version it was
  // drawn from (the server refuses a stale or blank base with 409).
  private loaded = false;
  private loadError = '';
  private version = '';
  private editing = false;
  private saving = false;

  constructor(container: HTMLElement) { this.container = container; }

  private canEdit(): boolean {
    try {
      const r = sessionStorage.getItem('realUserRole') || sessionStorage.getItem('userRole') || '';
      return ['admin', 'director', 'manager'].includes(r);
    } catch { return false; }
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    try {
      const r = await fetch('/api/team');
      const j = await r.json().catch(() => null);
      const ok = r.ok && !!j && Array.isArray(j.team) && !!j.version;
      this.team = ok ? j.team : [];
      this.oncall = ok ? (j.oncall || {}) : {};
      this.oncallLoaded = ok;
      this.loaded = ok;
      this.version = ok ? String(j.version) : '';
      this.loadError = ok ? '' : String((j && j.error) || "Couldn't load the team roster just now — nothing was changed. Reload the page in a moment.");
    } catch { this.team = []; this.oncall = {}; this.oncallLoaded = false; this.loaded = false; this.version = ''; this.loadError = "Couldn't load the team roster just now — nothing was changed. Reload the page in a moment."; }
    this.draw();
  }

  private blank(): Member {
    return { id: `stf_new_${Date.now().toString(36)}`, name: '', role: '', phone: '', email: '', team: '', cov: '', schedule: {}, notes: '' };
  }

  // ── R32 #2: ground on-call rotation card ──────────────────────────────────
  private renderOncall(edit: boolean): string {
    const tk = todayKey();
    const ground = this.team.filter(m => m.team === 'ground');
    // Phone lookup for the on-call name: exact roster match first, then a
    // UNIQUE prefix / first-token match among GROUND members only — an
    // ambiguous prefix must show no number rather than someone else's.
    const phoneOf = (name: string): string => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return '';
      const nm = (x: Member) => String(x.name || '').trim().toLowerCase();
      const exact = this.team.find(x => nm(x) === n);
      if (exact) return exact.phone;
      const tok = n.split(/[\s.]+/)[0];
      const cands = ground.filter(x => nm(x).startsWith(n) || (tok && nm(x).split(/\s+/)[0] === tok));
      return cands.length === 1 ? cands[0].phone : '';
    };
    const cells = DAYS.map(([k, l]) => {
      const who = String(this.oncall[k] || '').trim();
      const phone = phoneOf(who);
      if (edit) {
        return `<div class="tm-oc-cell ${k === tk ? 'today' : ''}">
          <div class="tm-oc-day">${l}</div>
          <input data-tm-oncall="${k}" list="tm-oc-names" value="${esc(who)}" placeholder="—" style="width:100%; box-sizing:border-box; border:1px solid var(--border); border-radius:8px; padding:8px; font-size:13px; text-align:center; font-family:inherit;">
        </div>`;
      }
      return `<div class="tm-oc-cell ${k === tk ? 'today' : ''}">
        <div class="tm-oc-day">${l}${k === tk ? ' · today' : ''}</div>
        <div class="tm-oc-name">${who ? esc(who) : '<span style="color:var(--text-faint); font-weight:400;">—</span>'}</div>
        ${phone ? `<a class="tm-oc-phone" href="tel:${esc(phone.replace(/[^\d+]/g, ''))}">📞 ${esc(phone)}</a>` : ''}
      </div>`;
    }).join('');
    return `
      <div class="tm-card">
        <div class="tm-card-head">🚨 Ground on-call ${edit ? '<span class="tm-card-sub">who takes ground emergencies each day — type a name (ground roster suggested)</span>' : '<span class="tm-card-sub">who takes ground emergencies today</span>'}</div>
        <div class="tm-oc-row">${cells}</div>
        ${edit ? `<datalist id="tm-oc-names">${ground.map(m => `<option value="${esc(m.name)}">`).join('')}</datalist>` : ''}
      </div>`;
  }

  // ── R32 #1: 24-hour coverage — Guest Services vs Ops Management ───────────
  private renderCoverage(): string {
    const tk = todayKey();
    const gsMembers = this.team.filter(m => ['gs', 'both'].includes(covGroup(m)));
    const omMembers = this.team.filter(m => ['om', 'both'].includes(covGroup(m)));
    if (!gsMembers.length && !omMembers.length) {
      return this.canEdit() ? `
        <div class="tm-card">
          <div class="tm-card-head">🕐 24-hour coverage — Guest Services vs Ops Management</div>
          <p class="tm-cov-foot" style="margin-top:0;">No one counts toward coverage yet. Roles containing "Guest" / "Overnight" count as Guest Services and "Operations" as Ops Management automatically — or set the Coverage column per person in ✏️ Edit roster.</p>
        </div>` : '';
    }
    // A day's mask = that day's shifts (clipped to midnight) + the spill-over
    // of the PREVIOUS day's overnight shifts — Monday 12a–6a coverage comes
    // from Sunday's "10p - 6a" cell, not Monday's.
    const PREV: Record<string, string> = { mon: 'sun', tue: 'mon', wed: 'tue', thu: 'wed', fri: 'thu', sat: 'fri', sun: 'sat' };
    const union = (ms: Member[], k: string): boolean[] => {
      const acc: boolean[] = Array(24).fill(false);
      for (const m of ms) {
        for (const [s, e] of parseShifts(m.schedule?.[k] || '')) markRange(acc, s, Math.min(e, 24));
        for (const [, e] of parseShifts(m.schedule?.[PREV[k]] || '')) if (e > 24) markRange(acc, 0, e - 24);
      }
      return acc;
    };
    const scale = `<div class="tm-cov-scale">${[0, 3, 6, 9, 12, 15, 18, 21].map(h => `<span>${fmtH(h)}</span>`).join('')}</div>`;
    const rows = DAYS.map(([k, l]) => {
      const gs = union(gsMembers, k), om = union(omMembers, k);
      const both = gs.map((v, h) => v && om[h]);
      const gsOnly = gs.map((v, h) => v && !om[h]);
      const omOnly = om.map((v, h) => !v ? false : !gs[h]);
      const none = gs.map((v, h) => !v && !om[h]);
      const n = (m: boolean[]) => m.filter(Boolean).length;
      const cells = Array.from({ length: 24 }, (_, h) => {
        const st = both[h] ? 'both' : gsOnly[h] ? 'gs' : omOnly[h] ? 'om' : 'none';
        const label = both[h] ? 'GS + Ops on' : gsOnly[h] ? 'GS only — acting Ops' : omOnly[h] ? 'Ops only — covering guest comms' : 'no one scheduled';
        return `<span class="tm-cov-c ${st}" title="${l} ${fmtH(h)}–${fmtH(h + 1)}: ${label}"></span>`;
      }).join('');
      const notes: string[] = [];
      if (n(gsOnly)) notes.push(`<span class="tm-cov-note gs">GS acting Ops: ${runsText(gsOnly)}</span>`);
      if (n(omOnly)) notes.push(`<span class="tm-cov-note om">Ops covering guest comms: ${runsText(omOnly)}</span>`);
      if (n(none)) notes.push(`<span class="tm-cov-note none">No one: ${runsText(none)}</span>`);
      return `
        <div class="tm-cov-row ${k === tk ? 'today' : ''}">
          <div class="tm-cov-day">${l}${k === tk ? ' ·' : ''}</div>
          <div class="tm-cov-main">
            <div class="tm-cov-strip">${cells}</div>
            <div class="tm-cov-meta">
              <span class="tm-cov-tot">GS <b>${n(gs)}h</b> · Ops <b>${n(om)}h</b> · both <b>${n(both)}h</b> · uncovered <b class="${n(none) ? 'bad' : ''}">${n(none)}h</b></span>
              ${notes.join('')}
            </div>
          </div>
        </div>`;
    }).join('');
    return `
      <div class="tm-card">
        <div class="tm-card-head">🕐 24-hour coverage — Guest Services vs Ops Management
          <span class="tm-legend">
            <span class="tm-leg"><span class="tm-cov-c both"></span> both on</span>
            <span class="tm-leg"><span class="tm-cov-c gs"></span> GS only (acting Ops)</span>
            <span class="tm-leg"><span class="tm-cov-c om"></span> Ops only (covers guest comms)</span>
            <span class="tm-leg"><span class="tm-cov-c none"></span> no one</span>
          </span>
        </div>
        ${scale}
        ${rows}
        <p class="tm-cov-foot">Counts every schedule cell that reads like a time range ("8a - 8p", "9-5", "10p - 6a", split shifts "8a-12p &amp; 4p-8p"; overnight hours land on the NEXT morning's strip). Who counts toward which group comes from the role name (guest / overnight → GS, operations → Ops; ground people don't auto-count) — override per person with the Coverage column while editing. GS: ${gsMembers.map(m => esc(m.name)).join(', ') || '—'} · Ops: ${omMembers.map(m => esc(m.name)).join(', ') || '—'}.</p>
      </div>`;
  }

  private draw(): void {
    if (!this.loaded) {
      // never "No staff added yet" + Edit roster over a roster that didn't load
      this.container.innerHTML = `
        <div class="page-content">
          <div class="dash-head"><h1>Team</h1></div>
          <div class="kpi-card" role="alert" style="padding:18px 20px; display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
            <div style="flex:1 1 240px; font-size:13px; color:var(--danger-text);">${esc(this.loadError)}</div>
            <button class="btn btn-primary btn-sm" id="tm-reload" style="min-height:36px;">Reload</button>
          </div>
        </div>`;
      this.container.querySelector('#tm-reload')?.addEventListener('click', () => { void this.render(); });
      return;
    }
    const edit = this.editing && this.canEdit();
    const tk = todayKey();
    // Ground first (on-call lives there), then remote, then unassigned —
    // original order preserved within each group. Editing keeps raw order so
    // row inputs never jump mid-typing.
    const rank = (m: Member) => m.team === 'ground' ? 0 : m.team === 'remote' ? 1 : 2;
    const ordered = edit ? this.team : [...this.team].sort((a, b) => rank(a) - rank(b));
    const cell = (m: Member, field: string, value: string, width = '') =>
      edit
        ? `<td><input data-tm="${esc(m.id)}|${esc(field)}" value="${esc(value)}" style="${width ? `width:${width};` : 'width:100%;'} box-sizing:border-box; border:1px solid var(--border); border-radius:6px; padding:6px 8px; font-size:13px; font-family:inherit;"></td>`
        : `<td>${esc(value) || '<span style="color:var(--text-faint);">—</span>'}</td>`;

    this.container.innerHTML = `
      <div class="page-content">
        <style>
          /* no overflow:hidden here — it would make the table its own clip
             box and defeat the sticky Name column (sticks to .u-table-wrap) */
          .tm-tbl { width:100%; min-width:1150px; border-collapse:separate; border-spacing:0; font-size:13.5px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); }
          .tm-tbl th { padding:11px 12px; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); border-bottom:1px solid var(--border); text-align:left; white-space:nowrap; }
          .tm-tbl td { padding:11px 12px; border-bottom:1px solid var(--border-soft, #f1f5f9); vertical-align:middle; line-height:1.4; }
          .tm-tbl td:first-child { font-weight:600; font-size:14px; }
          /* R33 #1: the Name column stays frozen while the week scrolls */
          .tm-tbl th:first-child, .tm-tbl td:first-child { position:sticky; left:0; background:var(--card); z-index:2; box-shadow:2px 0 0 var(--border-soft, #f1f5f9); min-width:130px; }
          .tm-tbl th:first-child { z-index:3; }
          .tm-tbl td.tm-day { text-align:center; white-space:nowrap; min-width:82px; }
          .tm-tbl tr:last-child td { border-bottom:none; }
          .tm-off { color:var(--text-faint); }
          .tm-today { background:var(--bg-subtle, #f8fafc); }
          th.tm-today { color:var(--text); }
          .tm-pill { font-size:10.5px; font-weight:700; border-radius:999px; padding:3px 9px; white-space:nowrap; }
          .tm-ground { background:#dcfce7; color:#166534; }
          .tm-remote { background:#dbeafe; color:#3730a3; }
          .tm-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius, 14px); padding:14px 16px; margin-bottom:14px; }
          .tm-card-head { font-size:13.5px; font-weight:700; margin-bottom:10px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
          .tm-card-sub { font-size:11.5px; font-weight:500; color:var(--text-muted); }
          /* on-call */
          .tm-oc-row { display:grid; grid-template-columns:repeat(7, 1fr); gap:8px; }
          .tm-oc-cell { border:1px solid var(--border); border-radius:10px; padding:9px 8px; text-align:center; min-width:0; overflow:hidden; }
          .tm-oc-cell.today { border-color:var(--danger, #dc2626); background:#fef2f2; }
          .tm-oc-day { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:4px; }
          .tm-oc-cell.today .tm-oc-day { color:var(--danger-text, #b91c1c); }
          .tm-oc-name { font-size:14px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .tm-oc-phone { display:inline-block; font-size:11.5px; font-weight:600; color:var(--info, #1d4ed8); text-decoration:none; margin-top:3px; white-space:nowrap; max-width:100%; overflow:hidden; text-overflow:ellipsis; vertical-align:bottom; }
          /* coverage */
          .tm-legend { display:inline-flex; gap:12px; flex-wrap:wrap; font-size:11px; font-weight:500; color:var(--text-muted); }
          .tm-leg { display:inline-flex; align-items:center; gap:4px; }
          .tm-leg .tm-cov-c { width:12px; height:12px; flex:none; }
          .tm-cov-scale { display:flex; justify-content:space-between; font-size:10px; color:var(--text-faint); margin:0 0 3px 52px; }
          .tm-cov-row { display:flex; gap:10px; align-items:flex-start; padding:7px 0; border-top:1px solid var(--border-soft, #f1f5f9); }
          .tm-cov-row.today { background:var(--bg-subtle, #f8fafc); border-radius:8px; }
          .tm-cov-day { flex:0 0 42px; font-size:12.5px; font-weight:700; padding-top:2px; }
          .tm-cov-main { flex:1; min-width:0; }
          .tm-cov-strip { display:flex; gap:1px; }
          .tm-cov-c { flex:1; height:20px; border-radius:2px; min-width:4px; display:inline-block; }
          .tm-cov-c.both { background:#16a34a; }
          .tm-cov-c.gs { background:#2563eb; }
          .tm-cov-c.om { background:#d97706; }
          .tm-cov-c.none { background:#fecaca; }
          .tm-cov-meta { display:flex; gap:10px; flex-wrap:wrap; margin-top:4px; font-size:11.5px; color:var(--text-muted); }
          .tm-cov-tot b { color:var(--text); }
          .tm-cov-tot b.bad { color:var(--danger-text, #b91c1c); }
          .tm-cov-note { font-weight:600; }
          .tm-cov-note.gs { color:#2563eb; }
          .tm-cov-note.om { color:#b45309; }
          .tm-cov-note.none { color:var(--danger-text, #b91c1c); }
          .tm-cov-foot { font-size:11px; color:var(--text-faint); margin:10px 0 0; line-height:1.5; }
          @media (max-width: 760px) {
            .tm-oc-row { grid-template-columns:repeat(4, 1fr); }
            .tm-cov-scale { margin-left:0; }
            .tm-cov-row { flex-direction:column; gap:4px; }
          }
          @media (max-width: 480px) {
            .tm-oc-row { grid-template-columns:repeat(2, 1fr); }
          }
        </style>
        <div class="dash-head">
          <h1>Team</h1>
          <p>Who's working when, and how to reach them — schedules and contact info for the whole staff, visible to everyone. ${this.canEdit() ? 'Office roles can edit.' : ''}</p>
        </div>
        <div style="display:flex; gap:8px; margin-bottom:12px;">
          ${this.canEdit() ? (edit
            ? `<button class="btn btn-primary btn-sm" id="tm-save" ${this.saving ? 'disabled' : ''}>${this.saving ? 'Saving…' : 'Save roster'}</button>
               <button class="btn btn-secondary btn-sm" id="tm-add">+ Add person</button>
               <button class="btn btn-secondary btn-sm" id="tm-cancel">Cancel</button>`
            : `<button class="btn btn-secondary btn-sm" id="tm-edit">✏️ Edit roster</button>`) : ''}
        </div>
        ${this.renderOncall(edit)}
        ${this.renderCoverage()}
        <div class="u-table-wrap">
          <table class="tm-tbl">
            <thead><tr>
              <th>Name</th><th>Team</th><th>Role</th>${edit ? '<th title="Which coverage strip this person counts toward">Coverage</th>' : ''}<th>Phone</th><th>Email</th>
              ${DAYS.map(([k, l]) => `<th style="text-align:center;" class="${k === tk ? 'tm-today' : ''}">${l}${k === tk ? ' ·' : ''}</th>`).join('')}
              <th>Notes</th>${edit ? '<th></th>' : ''}
            </tr></thead>
            <tbody>
              ${ordered.length ? ordered.map(m => `
                <tr>
                  ${cell(m, 'name', m.name, '130px')}
                  ${edit
                    ? `<td><select data-tm-team="${esc(m.id)}" style="border:1px solid var(--border); border-radius:6px; padding:6px; font-size:12.5px; font-family:inherit;"><option value="" ${!m.team ? 'selected' : ''}>—</option><option value="ground" ${m.team === 'ground' ? 'selected' : ''}>Ground</option><option value="remote" ${m.team === 'remote' ? 'selected' : ''}>Remote</option></select></td>`
                    : `<td>${m.team === 'ground' ? '<span class="tm-pill tm-ground">Ground</span>' : m.team === 'remote' ? '<span class="tm-pill tm-remote">Remote</span>' : '<span style="color:var(--text-faint);">—</span>'}</td>`}
                  ${cell(m, 'role', m.role, '120px')}
                  ${edit
                    ? `<td><select data-tm-cov="${esc(m.id)}" style="border:1px solid var(--border); border-radius:6px; padding:6px; font-size:12.5px; font-family:inherit;"><option value="" ${!m.cov ? 'selected' : ''}>auto (${{ gs: 'GS', om: 'Ops', both: 'both', '': 'none' }[covGroup({ ...m, cov: '' })]})</option><option value="gs" ${m.cov === 'gs' ? 'selected' : ''}>Guest Services</option><option value="om" ${m.cov === 'om' ? 'selected' : ''}>Ops Mgmt</option><option value="both" ${m.cov === 'both' ? 'selected' : ''}>Both</option></select></td>`
                    : ''}
                  ${cell(m, 'phone', m.phone, '115px')}
                  ${cell(m, 'email', m.email, '170px')}
                  ${DAYS.map(([k]) => edit
                    ? `<td class="tm-day ${k === tk ? 'tm-today' : ''}"><input data-tm="${esc(m.id)}|sched.${k}" list="tm-shift-suggest" value="${esc(m.schedule?.[k] || '')}" placeholder="off" style="width:92px; box-sizing:border-box; border:1px solid var(--border); border-radius:6px; padding:7px 6px; font-size:13px; text-align:center; font-family:inherit;"></td>`
                    : `<td class="tm-day ${k === tk ? 'tm-today' : ''}">${m.schedule?.[k] ? esc(m.schedule[k]) : '<span class="tm-off">off</span>'}</td>`).join('')}
                  ${cell(m, 'notes', m.notes)}
                  ${edit ? `<td><button data-tm-rm="${esc(m.id)}" style="border:none; background:none; cursor:pointer; color:var(--danger);" title="Remove">✕</button></td>` : ''}
                </tr>`).join('')
              : `<tr><td colspan="${13 + (edit ? 2 : 0)}" style="text-align:center; color:var(--text-muted); padding:24px;">No staff added yet${this.canEdit() ? ' — click "Edit roster" then "+ Add person"' : ''}.</td></tr>`}
            </tbody>
          </table>
        </div>
        ${edit ? `<datalist id="tm-shift-suggest">${[...new Set([...SHIFT_PRESETS, ...this.team.flatMap(m => DAYS.map(([k]) => canonicalShift(m.schedule?.[k] || ''))).filter(Boolean)])].map(v => `<option value="${esc(v)}">`).join('')}</datalist>` : ''}
        <p style="font-size:11.5px; color:var(--text-faint); margin-top:8px;">Schedule cells hold whatever reads best — pick a suggested shift or type your own; anything that reads like a time range is tidied to "8a - 8p" form automatically and feeds the coverage strips above (blank = off, "on call" stays as typed). The Name column stays put while the week scrolls. Ground rows sort first; the ground on-call rotation lives in its own card at the top. Today's column is highlighted. Cleaner dispatch capacity still lives in Housekeeping → Scheduler.</p>
      </div>`;
    this.bind();
  }

  private bind(): void {
    // R33 #1: tidy time text to canonical "8a - 8p" form on blur — uniform
    // cells with zero extra clicks ("on call"/notes pass through untouched).
    this.container.querySelectorAll<HTMLInputElement>('[data-tm]').forEach(inp => {
      const field = (inp.dataset.tm || '').split('|')[1] || '';
      if (field.startsWith('sched.')) inp.addEventListener('change', () => { inp.value = canonicalShift(inp.value); });
    });
    this.container.querySelector('#tm-edit')?.addEventListener('click', () => { this.editing = true; this.draw(); });
    this.container.querySelector('#tm-cancel')?.addEventListener('click', async () => { this.editing = false; await this.render(); });
    this.container.querySelector('#tm-add')?.addEventListener('click', () => { this.collect(); this.team.push(this.blank()); this.draw(); });
    this.container.querySelectorAll<HTMLElement>('[data-tm-rm]').forEach(btn => btn.addEventListener('click', () => {
      this.collect();
      this.team = this.team.filter(m => m.id !== btn.dataset.tmRm);
      this.draw();
    }));
    this.container.querySelector('#tm-save')?.addEventListener('click', async () => {
      this.collect();
      // the server drops nameless rows — block the save rather than silently
      // losing a row someone filled out but forgot to name
      const hasData = (m: any) => [m.phone, m.email, m.notes, m.team, m.cov, m.role, ...Object.values(m.schedule || {})].some(v => String(v || '').trim());
      const nameless = this.team.filter(m => !String(m.name || '').trim() && hasData(m));
      if (nameless.length) {
        alert(`${nameless.length} row${nameless.length === 1 ? ' has' : 's have'} details but no name. Add a name (or remove the row) — rows without a name are not saved.`);
        return;
      }
      if (!this.loaded || !this.version) { alert("The roster didn't load — reload the page, then redo your change."); return; }
      this.saving = true; this.draw();
      try {
        const r = await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team: this.team, ...(this.oncallLoaded ? { oncall: this.oncall } : {}), base_version: this.version }) });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || 'save failed');
        this.team = j.team || this.team;
        this.oncall = j.oncall || this.oncall;
        if (j.version) this.version = String(j.version);
        this.editing = false;
      } catch (e: any) { alert(`Save failed: ${e.message}`); }
      this.saving = false;
      this.draw();
    });
  }

  // Pull every input's current value into state (called before any redraw so
  // add/remove can't eat in-progress typing).
  private collect(): void {
    this.container.querySelectorAll<HTMLInputElement>('[data-tm]').forEach(inp => {
      const [id, field] = inp.dataset.tm!.split('|');
      const m = this.team.find(x => x.id === id);
      if (!m) return;
      if (field.startsWith('sched.')) {
        m.schedule = m.schedule || {};
        m.schedule[field.slice(6)] = inp.value;
      } else (m as any)[field] = inp.value;
    });
    this.container.querySelectorAll<HTMLSelectElement>('[data-tm-team]').forEach(sel => {
      const m = this.team.find(x => x.id === sel.dataset.tmTeam);
      if (m) m.team = sel.value;
    });
    this.container.querySelectorAll<HTMLSelectElement>('[data-tm-cov]').forEach(sel => {
      const m = this.team.find(x => x.id === sel.dataset.tmCov);
      if (m) m.cov = sel.value;
    });
    this.container.querySelectorAll<HTMLInputElement>('[data-tm-oncall]').forEach(inp => {
      this.oncall[inp.dataset.tmOncall!] = inp.value;
    });
  }
}
